<template>
  <TTabsToggle
    :header="$t('forms.settings_element_divider')"
    no-move
    :changed="wasChanged"
    data-testid="divider-settings"
  >
    <menu-content-container :padding-left-right="8">
      <content-row>
        <t-input-border
          data-testid="divider-style"
          :color="borderColor.model"
          :size="borderWidth.model"
          :border-style="borderStyle.model"
          :label="$t('forms.divider_style')"
          :label-width="$t('forms.border_width')"
          :label-style="$t('forms.border_style')"
          :template-colors-label="$t('color_picker.template_colors')"
          :predefined-colors-label="$t('color_picker.predefined_colors')"
          :light="generalStore.light"
          :colors="colors"
          :template-colors="templateColors"
          :changed-color="borderColor.changed"
          :changed-size="borderWidth.changed"
          :changed-style="borderStyle.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @border-style-changed="borderStyle.model = $event"
          @size-changed="borderWidth.model = $event"
          @color-changed="borderColor.model = $event"
          @get-template-colors="getAllColorsFromTemplate"
          @reset-color="borderColor.reset"
          @reset-size="borderWidth.reset"
          @reset-style="borderStyle.reset"
        ></t-input-border>
      </content-row>
      <content-row class="!items-end">
        <color-picker
          :label="$t('forms.section_bgc')"
          data-testid="divider-bg-color"
          :default-color="containerBgColor.model"
          :changed="containerBgColor.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="containerBgColor.model = $event"
          @reset="containerBgColor.reset"
        ></color-picker>
        <TButton
          v-if="!containerGradient.model"
          data-testid="add-gradient"
          @click="
            containerGradient.model =
              'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)'
          "
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TTabsToggle
          v-if="containerGradient.model"
          :header="$t('forms.gradient')"
          :changed="containerGradient.changed"
          default-open
          no-move
          show-remove
          can-be-reset
          @remove-block="containerGradient.model = undefined"
          @reset="containerGradient.reset"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="containerGradient.model" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>
      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full :padding-left-right="8">
      <content-row>
        <t-input-number
          v-model="paddingTopBottom"
          unit=""
          :min="10"
          :label="$t('forms.text_margin_tb')"
          :changed="paddingTbChanged"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="resetPaddingTb"
        ></t-input-number>
        <t-input-number
          v-model="paddingLeftRight"
          unit=""
          :label="$t('forms.text_margin_lr')"
          :changed="paddingLrChanged"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="resetPaddingLr"
        ></t-input-number>
      </content-row>
    </menu-content-container>
  </TTabsToggle>
</template>

<script setup lang="ts">
  import colorPicker from "@/forms/elements/colorPicker.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useColorsStore } from "@/stores/colors";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import {
    TTabsToggle,
    TInputNumber,
    TInputBorder,
    TButton,
    TNote,
  } from "@topol/twig-ui";
  import { computed, ref } from "vue";

  const mjmlStore = useMjmlStore();
  const colorsStore = useColorsStore();
  const generalStore = useGeneralStore();

  const { str, px, nullable, anyChanged, anyPropChanged } = useBlockAttr({
    mode: "global",
    block: "mj-divider",
  });

  // --- Border ---
  const borderColor = str("border-color", "#000000");
  const borderWidth = px("border-width", 1);
  const borderStyle = str("border-style", "solid");
  const padding = str("padding");

  const borderChanged = anyChanged(borderColor, borderWidth, borderStyle);

  function resetBorderSettings() {
    borderColor.reset();
    borderWidth.reset();
    borderStyle.reset();
  }

  // --- Container background ---
  const containerBgColor = str("container-background-color", "");
  const containerGradient = nullable("container-gradient");

  // --- Padding (custom tb/lr splitting) ---
  const defaultPaddingParts = computed(() => {
    const raw = padding.defaultValue || "10px 10px";
    const parts = String(raw).split(" ");
    return {
      tb: parseInt(parts[0]) || 10,
      lr: parseInt(parts[1] ?? parts[0]) || 10,
    };
  });

  const paddingParts = computed(() => {
    const raw = padding.model || padding.defaultValue || "10px 10px";
    const parts = String(raw).split(" ");
    return {
      tb: parseInt(parts[0]) || 10,
      lr: parseInt(parts[1] ?? parts[0]) || 10,
    };
  });

  const paddingTbChanged = computed(
    () => paddingParts.value.tb !== defaultPaddingParts.value.tb
  );

  const paddingLrChanged = computed(
    () => paddingParts.value.lr !== defaultPaddingParts.value.lr
  );

  function updatePadding(tb: number, lr: number) {
    padding.model = `${tb}px ${lr}px`;
  }

  function resetPaddingTb() {
    updatePadding(defaultPaddingParts.value.tb, paddingParts.value.lr);
  }

  function resetPaddingLr() {
    updatePadding(paddingParts.value.tb, defaultPaddingParts.value.lr);
  }

  const paddingTopBottom = computed({
    get() {
      return paddingParts.value.tb;
    },
    set(value: number) {
      updatePadding(value, paddingParts.value.lr);
    },
  });

  const paddingLeftRight = computed({
    get() {
      return paddingParts.value.lr;
    },
    set(value: number) {
      updatePadding(paddingParts.value.tb, value);
    },
  });

  // --- Colors ---
  const templateColors = ref<string[]>([]);
  const colors = computed(() => colorsStore.colors);

  function getAllColorsFromTemplate() {
    const templateString = JSON.stringify(mjmlStore.getMjml()).toLowerCase();
    const hexColorPattern = /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/g;
    const colors = templateString.match(hexColorPattern);
    // templateColors.value = [...new Set(colors)];
  }

  // --- Was changed ---
  const wasChanged = computed(() => {
    return (
      anyChanged(
        borderColor,
        borderWidth,
        borderStyle,
        containerBgColor,
        containerGradient
      ).value || anyPropChanged("padding").value
    );
  });
</script>
